import { useApiClient } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import type { Language } from '@/config/objects/languages';

interface LanguageSelectorProps {
  selectedLanguage: string;
  onLanguageChange: (languageCode: string) => void;
  className?: string;
}

/**
 * Language selector for tour viewer
 * Allows users to select their preferred language to view themed content
 */
export function LanguageSelector({
  selectedLanguage,
  onLanguageChange,
  className = '',
}: LanguageSelectorProps) {
  const apiClient = useApiClient();

  const { data: languagesResponse, isLoading } = useQuery({
    queryKey: ['Language'],
    queryFn: async () => {
      return apiClient.get<{ data: Language[] }>('/languages?limit=100');
    },
  });

  const languages = languagesResponse?.data;

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Globe className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading languages...</span>
      </div>
    );
  }

  if (!languages || languages.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Globe className="h-4 w-4 text-muted-foreground" />
      <select
        value={selectedLanguage}
        onChange={(e) => onLanguageChange(e.target.value)}
        className="px-3 py-1.5 text-sm border rounded-md bg-background"
        aria-label="Select language"
      >
        {languages.map((language: Language) => (
          <option key={language.id} value={language.code}>
            {language.name} ({language.code.toUpperCase()})
            {language.isDefault ? ' • Default' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}
